import {ChangeDetectionStrategy, Component, EventEmitter, Input, Output} from '@angular/core';
import {NgClass} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {formatDistanceToNow} from 'date-fns';
import {Button} from 'primeng/button';
import {InputText} from 'primeng/inputtext';
import {Tooltip} from 'primeng/tooltip';
import {Tag} from 'primeng/tag';
import {ConfirmationService, MenuItem, MenuItemCommandEvent} from 'primeng/api';
import {Menu} from 'primeng/menu';
import {Dialog} from 'primeng/dialog';
import {CosoChat} from '../datamodel';
import {CosoConfig} from '../../coso-config/datamodel';

@Component({
  selector: 'app-coso-chat-list',
  imports: [
    FormsModule,
    Button,
    InputText,
    Tooltip,
    Tag,
    NgClass,
    Menu,
    Dialog
  ],
  templateUrl: './coso-chat-list.component.html',
  styleUrl: './coso-chat-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CosoChatListComponent {
  @Input() chats: CosoChat[] = [];
  @Input() allowedCosoConfigs: CosoConfig[] = [];
  @Input() selectedChat: CosoChat | null = null;
  @Input() loading = false;
  @Input() creating = false;
  @Input() deletingChatId: number | undefined;
  @Input() renamingChatId: number | undefined;
  @Output() createChat = new EventEmitter<Event>();
  @Output() selectChat = new EventEmitter<CosoChat>();
  @Output() deleteChat = new EventEmitter<CosoChat>();
  @Output() renameChat = new EventEmitter<CosoChat>();

  searchTerm = '';
  actionChat: CosoChat | null = null;
  renameDialogVisible = false;
  renamingChat: CosoChat | null = null;
  renameTitle = '';
  chatActionItems: MenuItem[] = [
    {
      label: 'Rename',
      icon: 'pi pi-pencil',
      command: (event: MenuItemCommandEvent) => this.openRenameDialog(event.originalEvent)
    },
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      command: (event: MenuItemCommandEvent) => this.confirmDelete(event.originalEvent, this.actionChat)
    }
  ];

  constructor(private confirmationService: ConfirmationService) {
  }

  get filteredChats(): CosoChat[] {
    const query = this.searchTerm.trim().toLowerCase();
    if (!query) {
      return this.sortChats(this.chats);
    }

    return this.sortChats(this.chats.filter(chat => {
      const title = chat.chat_title?.toLowerCase() ?? '';
      const language = chat.language?.toLowerCase() ?? '';
      const config = this.getConfigHint(chat).toLowerCase();
      return title.includes(query) || language.includes(query) || config.includes(query);
    }));
  }

  hasActiveSearch(): boolean {
    return !!this.searchTerm.trim();
  }

  getConfigHint(chat: CosoChat): string {
    const config = this.allowedCosoConfigs.find(cosoConfig => cosoConfig.coso_config_id === chat.id_coso_config);
    return config?.flow || config?.module_name || 'Default context';
  }

  getLanguageLabel(chat: CosoChat): string {
    return (chat.language || 'it').toUpperCase();
  }

  getRelativeDate(chat: CosoChat): string {
    const chatDate = chat.m_date || chat.c_date;
    if (!chatDate) {
      return '';
    }

    const date = new Date(chatDate);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return formatDistanceToNow(date, {addSuffix: true});
  }

  trackChat(_index: number, chat: CosoChat): number | string {
    return chat.coso_chats_id ?? `${chat.chat_title}-${chat.c_date}`;
  }

  private sortChats(chats: CosoChat[]): CosoChat[] {
    return [...chats].sort((a, b) => {
      const aDate = a.m_date || a.c_date;
      const bDate = b.m_date || b.c_date;
      return (bDate ? new Date(bDate).getTime() : 0) - (aDate ? new Date(aDate).getTime() : 0);
    });
  }

  openChatActions(event: Event, chat: CosoChat, menu: Menu) {
    event.stopPropagation();
    this.actionChat = chat;
    menu.toggle(event);
  }

  confirmDelete(event: Event | undefined, chat: CosoChat | null) {
    event?.stopPropagation();
    if (!chat) {
      return;
    }

    this.confirmationService.confirm({
      target: event?.target as EventTarget,
      message: `Delete "${chat.chat_title || 'Untitled chat'}"? This action cannot be undone.`,
      header: 'Delete chat',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger',
      },
      accept: () => this.deleteChat.emit(chat)
    });
  }

  openRenameDialog(event?: Event) {
    event?.stopPropagation();
    if (!this.actionChat) {
      return;
    }

    this.renamingChat = this.actionChat;
    this.renameTitle = this.actionChat.chat_title || '';
    this.renameDialogVisible = true;
  }

  closeRenameDialog() {
    this.renameDialogVisible = false;
    this.renamingChat = null;
    this.renameTitle = '';
  }

  canRenameChat(): boolean {
    const nextTitle = this.renameTitle.trim();
    return !!this.renamingChat?.coso_chats_id
      && !!nextTitle
      && nextTitle !== (this.renamingChat.chat_title || '')
      && this.renamingChat.coso_chats_id !== this.renamingChatId;
  }

  submitRename() {
    if (!this.renamingChat || !this.canRenameChat()) {
      return;
    }

    this.renameChat.emit({
      ...this.renamingChat,
      chat_title: this.renameTitle.trim()
    });
    this.closeRenameDialog();
  }

  onChatRowKeyDown(event: KeyboardEvent, chat: CosoChat) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.selectChat.emit(chat);
  }
}
